
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';

interface UsageData {
  date: string;
  processedFiles: number;
  lastReset: string;
}

const STORAGE_KEY = 'image_compressor_usage';
const FREE_DAILY_LIMIT = 10;

export const useUsageTracking = (isPremium: boolean, user: User | null) => {
  const [dailyUsage, setDailyUsage] = useState<UsageData>({
    date: new Date().toDateString(),
    processedFiles: 0,
    lastReset: new Date().toISOString()
  });
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [loading, setLoading] = useState(true);

  const isAuthenticated = !!user;
  const currentLimit = FREE_DAILY_LIMIT;

  // Load usage data on init
  useEffect(() => {
    if (isPremium) {
      setLoading(false);
      return;
    }

    if (isAuthenticated) {
      // Authenticated users: fetch from DB
      fetchDbUsage();
    } else {
      // Anonymous users: use localStorage
      loadLocalUsage();
      setLoading(false);
    }
  }, [isPremium, isAuthenticated, user?.id]);

  const loadLocalUsage = () => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const data: UsageData = JSON.parse(stored);
        const today = new Date().toDateString();
        if (data.date !== today) {
          const resetData: UsageData = {
            date: today,
            processedFiles: 0,
            lastReset: new Date().toISOString()
          };
          setDailyUsage(resetData);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(resetData));
        } else {
          setDailyUsage(data);
        }
      } catch {
        resetLocalUsage();
      }
    }
  };

  const resetLocalUsage = () => {
    const resetData: UsageData = {
      date: new Date().toDateString(),
      processedFiles: 0,
      lastReset: new Date().toISOString()
    };
    setDailyUsage(resetData);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(resetData));
  };

  const fetchDbUsage = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);

      const { data, error } = await supabase
        .from('usage_tracking')
        .select('files_count')
        .eq('user_id', user.id)
        .gte('created_at', todayStart.toISOString());

      if (error) throw error;

      const totalFiles = (data || []).reduce((sum, row) => sum + (row.files_count || 0), 0);
      setDailyUsage({
        date: new Date().toDateString(),
        processedFiles: totalFiles,
        lastReset: todayStart.toISOString()
      });
    } catch (error) {
      console.error('Error fetching DB usage:', error);
    } finally {
      setLoading(false);
    }
  };

  // Track usage in anonymous_analytics (always) and usage_tracking (if authenticated)
  const trackUsage = useCallback(async (
    filesCount: number,
    originalBytes?: number,
    compressedBytes?: number
  ) => {
    // Always insert anonymous analytics via edge function (server-side validated)
    try {
      await supabase.functions.invoke('track-analytics', {
        body: { action_type: 'compression', files_count: filesCount }
      });
    } catch (e) {
      console.error('Error inserting anonymous analytics:', e);
    }

    if (isAuthenticated && user) {
      // Insert into usage_tracking
      try {
        await supabase
          .from('usage_tracking')
          .insert({
            user_id: user.id,
            action_type: 'compression',
            files_count: filesCount,
            total_original_bytes: originalBytes ?? null,
            total_compressed_bytes: compressedBytes ?? null,
          });
      } catch (e) {
        console.error('Error inserting usage_tracking:', e);
      }
    }
  }, [isAuthenticated, user]);

  const incrementProcessedFiles = useCallback((count: number = 1) => {
    if (isPremium) return;

    if (isAuthenticated) {
      // DB-tracked: just update local state, actual DB insert happens via trackUsage
      setDailyUsage(prev => ({
        ...prev,
        processedFiles: prev.processedFiles + count
      }));
    } else {
      // localStorage-tracked
      setDailyUsage(prev => {
        const newData: UsageData = {
          ...prev,
          processedFiles: prev.processedFiles + count
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newData));
        return newData;
      });
    }
  }, [isPremium, isAuthenticated]);

  const canProcessFiles = useCallback((count: number = 1) => {
    if (isPremium) return true;
    return dailyUsage.processedFiles + count <= currentLimit;
  }, [isPremium, dailyUsage.processedFiles, currentLimit]);

  const getRemainingFiles = useCallback(() => {
    if (isPremium) return Infinity;
    return Math.max(0, currentLimit - dailyUsage.processedFiles);
  }, [isPremium, dailyUsage.processedFiles, currentLimit]);

  const hasReachedLimit = useCallback(() => {
    if (isPremium) return false;
    return dailyUsage.processedFiles >= currentLimit;
  }, [isPremium, dailyUsage.processedFiles, currentLimit]);

  // Check if anonymous user needs to register
  const requiresRegistration = useCallback(() => {
    if (isPremium || isAuthenticated) return false;
    return dailyUsage.processedFiles >= FREE_DAILY_LIMIT;
  }, [isPremium, isAuthenticated, dailyUsage.processedFiles]);

  const resetUsage = useCallback(() => {
    if (!isAuthenticated) {
      resetLocalUsage();
    }
  }, [isAuthenticated]);

  return {
    dailyUsage,
    canProcessFiles,
    incrementProcessedFiles,
    getRemainingFiles,
    hasReachedLimit,
    resetUsage,
    trackUsage,
    freeLimit: currentLimit,
    anonymousLimit: FREE_DAILY_LIMIT,
    requiresRegistration,
    showRegistrationModal,
    setShowRegistrationModal,
    loading,
    isAuthenticated
  };
};
